import { Box, Card, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { When } from "react-if";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { SystemUserService } from "../../../services/system-user.service";
import { createAdminFormSchema } from "../../../schemas/admin-schema";
import { SystemUserTypes } from "../../../types";
import FormBuilder from "../../form-builder";
import { INVITE_ADMIN_USER_FORM_INPUTS, SYSTEM_USER_HOME_LOCATION_ADDRESS_INPUTS, SYSTEM_USER_TEAM_ASSIGNMENT_INPUTS } from "../../../builders/admins-builders";
import { customerPermissionFlags, listCustomPermissions, permissionToText } from "../../../constants/systemPermissions.constants";
import { createSystemUserPermissionCheck } from "../../../helpers/permissionCheck";
import { displayName } from "../../../helpers/displayName";
import { RootState } from "../../../store";
import { RoutingForUserNote } from '../../notification-rules/RoutingForUserNote';
import { useNotificationRulesEnabled } from '../../notification-rules/use-notification-rules-enabled';

interface Props {
    defaultValues?: SystemUserTypes.SystemUserFormValues,
    fromEdit?: boolean,
}

export const SystemUserForm: React.FC<Props> = ({
    fromEdit = false,
    defaultValues,
}) => {
    const navigate = useNavigate();
    const notificationRulesOn = useNotificationRulesEnabled();
    const teams = useSelector((state: RootState) => state.teams.items);
    const groups = useSelector((state: RootState) => state.groups.items);
    // The owning screen kicks off TeamService/GroupService.fetchAll on mount;
    // these flags are the only honest signal for whether that is still in
    // flight. Don't infer it from an empty items array — GroupService.fetchAll
    // also writes the teams slice (with just its group-nested teams), so a
    // non-empty slice does not mean the full team list has arrived.
    const teamsFetching = useSelector((state: RootState) => state.teams.loading.fetchAll);
    const groupsFetching = useSelector((state: RootState) => state.groups.loading.fetchAll);

    // Helper to determine if team/group fields should be shown based on role
    const shouldShowTeamFields = (userType: string) => {
        // Show teams for Team Leader, Group Leader, Admin, and Super Admin roles
        // VIEW_ONLY users cannot be assigned to teams
        return ['TEAM_LEADER', 'GROUP_LEADER', 'ADMIN', 'SUPER_ADMIN'].includes(userType);
    };

    const shouldShowGroupFields = (userType: string) => {
        // Show groups for Group Leader, Admin, and Super Admin roles
        // TEAM_LEADER can only manage teams, not groups
        // VIEW_ONLY users cannot be assigned to groups
        return ['GROUP_LEADER', 'ADMIN', 'SUPER_ADMIN'].includes(userType);
    };

    // Selectable options for the team and group dropdowns. Kept sourced from
    // their own slice so this fix doesn't widen what an admin can actually
    // assign. The `_id` fallback mirrors the rest of the app — some legacy
    // raw-mongo responses aren't normalised to `id`. Records with neither are
    // skipped rather than defaulted to '': FormBuilder turns every option into
    // a selectable MenuItem, and a blank value would be persisted straight into
    // the teamIds/groupIds arrays that scope a Team/Group Leader's access.
    const teamOptions = useMemo(
        () => teams.flatMap(team => {
            const id = team.id || (team as any)._id;
            return id
                ? [{ label: team.name || 'Unnamed Team', value: String(id) }]
                : [];
        }),
        [teams],
    );

    const groupOptions = useMemo(
        () => groups.flatMap(group => {
            const id = group.id || (group as any)._id;
            return id
                ? [{ label: group.name || 'Unnamed Group', value: String(id) }]
                : [];
        }),
        [groups],
    );

    // Display-only name lookup, deliberately wider than `teamOptions`:
    // GroupService.fetchAll overwrites the teams slice with only its
    // group-nested teams (services/groups.service.ts), so whichever of the two
    // fetches lands last decides what that slice holds. Reading names from both
    // slices makes the label independent of that race — without making the
    // extra teams assignable, which would hand an admin ids outside the set the
    // teams endpoint returned for this subscription.
    const teamNameById = useMemo(() => {
        const byId = new Map<string, string>();

        const add = (team: any) => {
            const id = team?.id || team?._id;
            // Gate on the name, not the key: a nameless entry in the first source
            // must not block a named copy of the same team in the second.
            if (!id || !team?.name || byId.get(String(id))) return;
            byId.set(String(id), team.name);
        };

        teams.forEach(add);
        groups.forEach(group => (group.teams || []).forEach(add));

        return byId;
    }, [teams, groups]);

    // A selected id with no resolvable name must never render as itself — that
    // is how a bare team ObjectId reached a user (#support, 2026-07-29). While a
    // fetch that could still supply the name is outstanding, say so; once they
    // have all settled the name genuinely can't be resolved (team deleted,
    // belonging to another subscription, or a failed fetch), so stay neutral
    // instead of asserting which of those it is.
    // Either fetch can populate a team name, so the Teams chip waits for both.
    const renderTeamChipLabel = (value: any) =>
        displayName(teamNameById.get(String(value)), teamsFetching || groupsFetching);

    // No second source to consult for groups: FormBuilder only calls this after
    // its own lookup over these same `groupOptions` missed, so re-scanning them
    // here could only miss again.
    const renderGroupChipLabel = () => displayName(undefined, groupsFetching);

    const formik = useFormik<SystemUserTypes.SystemUserFormValues>({
        initialValues: defaultValues ? {
            ...defaultValues,
            alertSettings: defaultValues.alertSettings ?? { email: false, sms: false, call: false, push: false },
            // Coerce stored nulls to '' — Home Location is optional, but the API
            // returns null for unset fields and the (non-nullable) Yup schema
            // rejects null, which blocked saving the edit form (#support 2026-07-29).
            homeLocation: {
                address: defaultValues.homeLocation?.address ?? '',
                state: defaultValues.homeLocation?.state ?? '',
                zip: defaultValues.homeLocation?.zip ?? '',
                city: defaultValues.homeLocation?.city ?? '',
            },
            teamIds: defaultValues.teamIds || [],
            groupIds: defaultValues.groupIds || [],
        } : {
            email: '',
            fullname: '',
            phone: '',
            userType: customerPermissionFlags.TEAM_LEADER,
            grantIntakeRiskAssessment: false,
            homeLocation: {},
            teamIds: [],
            groupIds: [],
            alertSettings: {
                email: true,
                sms: false,
                call: false,
                push: false,
            },
        },
        validationSchema: createAdminFormSchema,
        onSubmit: async (_values) => { 
            const values = {
                ..._values,
                phone: _values.phone.trim(),
            }  
            if (fromEdit) {
                await SystemUserService.update(
                    values as any, 
                    (isValid, message) => {
                        if (!isValid) {
                            toast(
                                message, 
                                {
                                    autoClose: 1500,
                                    type: 'error',
                                }
                            );
                            return;
                        }
    
                        formik.resetForm();
    
                        toast(
                            'User successfully updated.', 
                            {
                                autoClose: 1500,
                                type: 'success',
                            }
                        );
    
                        navigate('/system-users');
                    }
                );
                return;
            }
            await SystemUserService.create(
                values as any, 
                (isValid, message) => {
                    if (!isValid) {
                        toast(
                            message, 
                            {
                                autoClose: 1500,
                                type: 'error',
                            }
                        );
                        return;
                    }

                    formik.resetForm();

                    toast(
                        'An invitation was successfully sent to the email.', 
                        {
                            autoClose: 1500,
                            type: 'success',
                        }
                    );

                    navigate('/system-users');
                }
            );
        },
    });

    const { handleSubmit, isSubmitting, errors, touched, values, getFieldProps, setFieldValue } = formik;

    // "No Notifications" is derived from alertSettings — all four flags false IS the no-notifications state.
    // The checkbox acts as a one-way "clear all" action: clicking it when unchecked clears all alerts,
    // and the checkbox is then disabled so it cannot be un-toggled directly. To leave the
    // no-notifications state, the user clicks any alert type — which clears No Notifications
    // by virtue of an alert flag becoming true.
    const noNotifications = !(
        values?.alertSettings?.email === true ||
        values?.alertSettings?.sms === true ||
        values?.alertSettings?.call === true ||
        values?.alertSettings?.push === true
    );

    const handleNoNotificationChange = () => {
        setFieldValue('alertSettings', {
            email: false,
            sms: false,
            call: false,
            push: false,
        });
    }

    // Clear team/group assignments when role changes
    const handleRoleChange = (newRole: string) => {
        // A base-role change must NOT silently strip an already-granted Intake
        // Risk Assessment capability — leave grantIntakeRiskAssessment as-is
        // unless the new role IS Intake Specialist itself (then intake is the
        // base role, not an add-on, so there is nothing left to carry).
        if (newRole === customerPermissionFlags.INTAKE_RISK_ASSESSMENT) {
            setFieldValue('grantIntakeRiskAssessment', false);
        }
        setFieldValue('userType', newRole);

        // Clear assignments that are no longer valid for the new role
        if (!shouldShowTeamFields(newRole)) {
            setFieldValue('teamIds', []);
        }
        if (!shouldShowGroupFields(newRole)) {
            setFieldValue('groupIds', []);
        }
    };

    const intakeAddonSelected = !!values.grantIntakeRiskAssessment;
    const toggleIntakeAddon = () => setFieldValue('grantIntakeRiskAssessment', !intakeAddonSelected);

    return (
        <Card sx={{ mt: 2, p: 2 }}>
            <Typography variant="body1" fontWeight={500} color={'text.default'}>
                Personal Information (Required)
            </Typography>

            <Box mt={3}>
                <FormikProvider value={formik}>
                    <Form noValidate onSubmit={handleSubmit}>
                        <FormBuilder
                            errors={errors}
                            size="medium"
                            getFieldProps={getFieldProps}
                            touched={touched}
                            inputs={[...INVITE_ADMIN_USER_FORM_INPUTS]}
                        />
                    </Form>
                </FormikProvider>
               
               <Typography variant="body1" mt={3} mb={3} fontWeight={500} color={'text.default'}>
                    Home Location (Optional)
                </Typography>

                <FormBuilder
                    size="medium"
                    errors={errors}
                    getFieldProps={getFieldProps}
                    touched={touched}
                    inputs={[...SYSTEM_USER_HOME_LOCATION_ADDRESS_INPUTS]}
                />
            
                <Typography variant="body1" mt={3} mb={2} fontWeight={500} color={'text.default'}>
                    Role and Permissions (Required)
                </Typography>

                <FormControlLabel 
                    control={
                        <Checkbox 
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.SUPER_ADMIN)}
                            checked={values.userType === customerPermissionFlags.SUPER_ADMIN} 
                            onChange={() => handleRoleChange(customerPermissionFlags.SUPER_ADMIN)}
                        />
                    } 
                    label="Super Admin" 
                />
                <FormControlLabel 
                    control={
                        <Checkbox 
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.ADMIN)}
                            checked={values.userType === customerPermissionFlags.ADMIN} 
                            onChange={() => handleRoleChange(customerPermissionFlags.ADMIN)}
                        />
                    } 
                    label="Admin" 
                />
                <FormControlLabel 
                    control={
                        <Checkbox 
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.VIEW_ONLY)}
                            checked={values.userType === customerPermissionFlags.VIEW_ONLY} 
                            onChange={() => handleRoleChange(customerPermissionFlags.VIEW_ONLY)}
                        />
                    } 
                    label="View Only" 
                />
                <FormControlLabel 
                    control={
                        <Checkbox 
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.GROUP_LEADER)}
                            checked={values.userType === customerPermissionFlags.GROUP_LEADER} 
                            onChange={() => handleRoleChange(customerPermissionFlags.GROUP_LEADER)}
                        />
                    } 
                    label="Group Leader" 
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.TEAM_LEADER)}
                            checked={values.userType === customerPermissionFlags.TEAM_LEADER}
                            onChange={() => handleRoleChange(customerPermissionFlags.TEAM_LEADER)}
                        />
                    }
                    label="Team Leader"
                />
                {/* Intake Specialist = the user's SOLE role: they get only the
                    stripped Risk Assessment experience (Mode 2). To add IRA on
                    top of another role instead, pick that role and use the
                    "Can run Intake Risk Assessments" toggle below (Mode 1). */}
                <FormControlLabel
                    control={
                        <Checkbox
                            disabled={!createSystemUserPermissionCheck(customerPermissionFlags.INTAKE_RISK_ASSESSMENT)}
                            checked={values.userType === customerPermissionFlags.INTAKE_RISK_ASSESSMENT}
                            onChange={() => handleRoleChange(customerPermissionFlags.INTAKE_RISK_ASSESSMENT)}
                        />
                    }
                    label="Intake Specialist (Risk Assessment only)"
                />

                {/* Additive Mode 1: keep the chosen base role and also grant the
                    Risk Assessment tab. Hidden when Intake Specialist is the
                    role (redundant) and for actors who can't grant intake. */}
                <When condition={values.userType !== customerPermissionFlags.INTAKE_RISK_ASSESSMENT
                    && createSystemUserPermissionCheck(customerPermissionFlags.INTAKE_RISK_ASSESSMENT)}>
                    <Box mt={1}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={intakeAddonSelected}
                                    onChange={toggleIntakeAddon}
                                />
                            }
                            label="Can run Intake Risk Assessments"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                            Adds the Risk Assessment tab on top of this role.
                        </Typography>
                    </Box>
                </When>

                <When condition={shouldShowTeamFields(values.userType) || shouldShowGroupFields(values.userType)}>
                    <Typography variant="body1" mt={3} mb={2} fontWeight={500} color={'text.default'}>
                        Team and Group Assignments (Optional)
                    </Typography>
                    <Typography variant="body2" mb={2} color="text.secondary">
                        Assign this user to manage specific teams and/or groups based on their role.
                    </Typography>
                    
                    <FormBuilder
                        size="medium"
                        errors={errors}
                        getFieldProps={getFieldProps}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        inputs={SYSTEM_USER_TEAM_ASSIGNMENT_INPUTS.map(input => ({
                            ...input,
                            options: input.id === 'teamIds' ? teamOptions : groupOptions,
                            renderChipLabel: input.id === 'teamIds' ? renderTeamChipLabel : renderGroupChipLabel,
                            // Hide fields based on role
                            ...(input.id === 'teamIds' && !shouldShowTeamFields(values.userType) && { sx: { display: 'none' } }),
                            ...(input.id === 'groupIds' && !shouldShowGroupFields(values.userType) && { sx: { display: 'none' } }),
                            // Disable groups field for Team Leader role
                            ...(input.id === 'groupIds' && values.userType === customerPermissionFlags.TEAM_LEADER && { 
                                disabled: true
                            }),
                        }))}
                    />
                </When>

                <Typography variant="body1" mt={3} mb={0} fontWeight={500} color={'text.default'}>
                    Alert Notification Type (Required)
                </Typography>
                <Typography variant="body2" mb={2} color="text.secondary">
                    {notificationRulesOn
                        ? 'How POM reaches this person when an alert they receive happens. Choose one or more methods. Which alerts reach them is decided by their team assignments and by Settings, under Notifications.'
                        : 'Select how you would like to receive notifications for new alerts. You can choose one or more methods, or opt out of notifications entirely.'}
                </Typography>
                {notificationRulesOn && <RoutingForUserNote userId={(values as any)?.id ?? null} />}
                
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values?.alertSettings?.email === true}
                            defaultChecked={values?.alertSettings?.email}
                            onChange={() => {
                                setFieldValue('alertSettings', ({ ...values.alertSettings, email: !values.alertSettings?.email }));
                            }}
                        />
                    }
                    label="Email"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values?.alertSettings?.sms === true}
                            defaultChecked={values?.alertSettings?.sms}
                            onChange={() => {
                                setFieldValue('alertSettings', ({ ...values.alertSettings, sms: !values.alertSettings?.sms }));
                            }}
                        />
                    }
                    label="Phone SMS"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values?.alertSettings?.call === true}
                            defaultChecked={values?.alertSettings?.call}
                            onChange={() => {
                                setFieldValue('alertSettings', ({ ...values.alertSettings, call: !values.alertSettings?.call }));
                            }}
                        />
                    }
                    label="Phone Call"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values?.alertSettings?.push === true}
                            defaultChecked={values?.alertSettings?.push}
                            onChange={() => {
                                setFieldValue('alertSettings', ({ ...values.alertSettings, push: !values.alertSettings?.push }));
                            }}
                        />
                    }
                    label="Dashboard Push Notification"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={noNotifications}
                            disabled={noNotifications}
                            onChange={handleNoNotificationChange}
                        />
                    }
                    label="No Notifications"
                />
                <When condition={noNotifications}>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: -0.5 }}>
                        Select an alert type above to enable notifications.
                    </Typography>
                </When>

                <Grid marginTop={20} flexDirection={'row-reverse'} container spacing={2} mt={3}>
                    <LoadingButton
                        variant="contained"
                        sx={{ ml: 1 }}
                        onClick={() => handleSubmit()}
                        loading={isSubmitting}
                    >
                       {fromEdit ? 'Confirm Edit' : "Send Invitation"} 
                    </LoadingButton>
                    <LoadingButton
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            navigate('/system-users');
                        }}
                        disabled={isSubmitting}
                        loading={false}
                    >
                        Cancel
                    </LoadingButton>
                </Grid>

            </Box>
        </Card>
    )

}